import * as Permission from '../common/util/Permission';
import { StorageAdapter } from './utils/StorageAdapter.ts';

export const VIEW_MODE_PARTNER1 = 'partner1';
export const VIEW_MODE_PARTNER2 = 'partner2';
export const VIEW_MODE_DEVELOPER = 'developer';

/* this object can be used only in browser - in server we need multiple users */
let globalPermissionObject;

export function hasPermission(perm) {
    let permissionObject = globalPermissionObject;

    return Permission.hasPermissionInternal(perm, permissionObject);
}

export function hasOrganisationPermission(perm, organisationId) {
    let permissionObject = globalPermissionObject;

    return Permission.hasOrganisationPermissionInternal(perm, organisationId, permissionObject);
}


/**
 *
 * @param permissionObject
 */
export function setPermissionObject(permissionObject) {
    globalPermissionObject = permissionObject;
}

/**
 *
 * @returns permissionObject
 */
export function getPermissionObject() {
    return globalPermissionObject;
}

let developerMode = true;

/* read developer mode from local storage */
if (typeof window === 'object' && 'localStorage' in window) {
    const storedValue = StorageAdapter.getSync('developerMode');
    if (storedValue !== undefined && storedValue !== null) {
        developerMode = storedValue === "true" ? true : false;
    }
}



export function toggleDeveloperMode() {
    developerMode = !developerMode;

    if (typeof window === 'object' && 'localStorage' in window) {
        StorageAdapter.setSync('developerMode', String(developerMode));
    }

    return developerMode;
}

export function toggleViewMode(type) {
    if (typeof window === 'object' && 'localStorage' in window) {
        if (type === VIEW_MODE_PARTNER1 || type === VIEW_MODE_PARTNER2 || type === VIEW_MODE_DEVELOPER) {
            StorageAdapter.setSync('viewMode', type);
            console.log('viewMode', type);
        } else {
            StorageAdapter.removeSync('viewMode');
        }

        // Keep developer mode in sync with view mode selection so switching to "regular user"
        // actually reduces the UI (otherwise developerMode stays true in memory for the whole session).
        developerMode = type === VIEW_MODE_DEVELOPER;
        StorageAdapter.setSync('developerMode', String(developerMode));
    }
}

export function getViewMode() {
    if (typeof window === 'object' && 'localStorage' in window) {
        return StorageAdapter.getSync('viewMode');
    }
    return null;
}

export function getDeveloperMode() {
    return developerMode || getViewMode() === VIEW_MODE_DEVELOPER;
}

export function canHaveDeveloperMode() {
    return hasPermission(Permission.PERM_DEVELOPER) || hasPermission(Permission.PERM_INSTANCE_SUPPORT) || hasPermission(Permission.PERM_INSTANCE_ADMIN);
}

export function developerModeEnabled(organisationId) {
    if (getDeveloperMode() || getViewMode() === VIEW_MODE_DEVELOPER) {
        if (organisationId && hasOrganisationPermission(Permission.PERM_DEVELOPER, organisationId)) {
            return true;
        } else if (canHaveDeveloperMode()) {
            return true;
        }
    }
    return false;
}


export function isDeveloper(orgId) {
    return (canHaveDeveloperMode()) && (developerModeEnabled(orgId));
}

function getSnapshotCapability() {
    if (typeof document !== 'object' || !document.body || !document.body.dataset) {
        return 'off';
    }

    const configured = String(document.body.dataset.snapshotCapability || '').trim().toLowerCase();
    return configured === 'capture' || configured === 'full' ? configured : 'off';
}

function getSnapshotTestingUserIds() {
    if (typeof document !== 'object' || !document.body || !document.body.dataset) {
        return [];
    }

    return String(document.body.dataset.snapshotTestingUserIds || '')
        .split(',')
        .map((userId) => String(userId).trim().toLowerCase())
        .filter(Boolean);
}

export function isSnapshotTestingUserAllowed() {
    const allowedUserIds = getSnapshotTestingUserIds();

    if (allowedUserIds.length === 0) {
        return true;
    }

    const permissionObject = getPermissionObject();
    const currentUserId = permissionObject && permissionObject.id
        ? String(permissionObject.id).trim().toLowerCase()
        : '';

    return Boolean(currentUserId && allowedUserIds.includes(currentUserId));
}

function hasSnapshotTestingAccess(orgId) {
    if (hasPermission(Permission.PERM_DEVELOPER)) {
        return developerModeEnabled(orgId);
    }

    if (!hasPermission(Permission.PERM_INSTANCE_DEVELOPER)) {
        return false;
    }

    if (orgId) {
        let permissionObject = getPermissionObject();
        let organisationPermissions = permissionObject && Array.isArray(permissionObject.organisationPermissions)
            ? permissionObject.organisationPermissions
            : [];
        let hasOrganisationAccess = organisationPermissions.some((organisationPermission) => String(organisationPermission.organisation_id) === String(orgId));
        if (!hasOrganisationAccess) {
            return false;
        }
    }

    return getDeveloperMode() || getViewMode() === VIEW_MODE_DEVELOPER;
}

export function canCaptureSnapshotTesting(orgId) {
    return getSnapshotCapability() !== 'off'
        && isSnapshotTestingUserAllowed()
        && hasSnapshotTestingAccess(orgId);
}

export function canUseSnapshotTesting(orgId) {
    return getSnapshotCapability() === 'full'
        && isSnapshotTestingUserAllowed()
        && hasSnapshotTestingAccess(orgId);
}

export function isPartner2(orgId) {
    return hasPermission(Permission.PERM_PARTNER2) || isDeveloper(orgId) || getViewMode() === VIEW_MODE_PARTNER2;
}
export function isPartner1(orgId) {
    return hasPermission(Permission.PERM_PARTNER1) || isDeveloper(orgId) || isPartner2(orgId) || getViewMode() === VIEW_MODE_PARTNER1;
}
